// New Product Header for EN-10321 PDP structure A/B test
import React from 'react';
import { Product, Rating } from 'types/Product';
import Show from 'components/Functional/Show';
import { PriceObject } from '../ProductDetails/ProductPrice';
import { PDPMetatags } from '../ProductDetails/PDPMetaTags';
import YotpoStars from 'components/Yotpo/YotpoStars';
import { isNotServerSideRendering } from 'helpers/env';
import { formatPrice } from 'helpers/currency';
import {
  ProductHeaderContainer,
  ProductPromoTitle,
  PricingPromoContainer,
  OriginalPrice,
  StrikethroughPrice,
  ValuePropText
} from './styled';
import { PromoCode } from 'components/Strikethrough';
import { useShowNewPdpStructure } from '../../../../hooks/splits/useNewPdpStructureEnabled';

type ProductHeaderProps = {
  product: Product;
  productTitle: string;
  price: PriceObject;
  siteUrl: string;
  rating: Rating;
};

const valuePropText = {
  'layflat': 'A premium, handcrafted album with foil-stamped linen covers and custom, ultra-thick layflat pages.',
  'hardcover': 'The Hardcover Photo Book touts premium fabric binding, display-worthy dust jackets, and minimalist layouts for durability and timelessness.'
};

const ProductHeaderNew = ({ product, productTitle, price, siteUrl, rating }: ProductHeaderProps): JSX.Element => {
  const isNotSSR = isNotServerSideRendering();

  const showNewPdpStructure = useShowNewPdpStructure();

  const shouldDisplayPromoCode = price.promoCode?.length > 0;

  // TODO: Get value prop text for each product from Magento if test is baselined for other products
  const valueProp = valuePropText[product.sku];

  return (
    <ProductHeaderContainer>
      <PDPMetatags product={product} siteUrl={siteUrl || ''} />

      {rating && (
        <YotpoStars rating={rating} showNewPdpStructure={showNewPdpStructure} />
      )}

      <ProductPromoTitle data-testid="product-title">{productTitle}</ProductPromoTitle>

      <PricingPromoContainer strikethrough={!!price.strikethroughPrice}>
        {/* Initial price from SSR may be different from actual price due to product options, */}
        {/* so we can't display price until CSR kicks in */}
        <Show when={isNotSSR}>
          <Show when={!!price.strikethroughPrice}>
            <StrikethroughPrice>
              From {formatPrice(price.strikethroughPrice)}
            </StrikethroughPrice>
          </Show>

          <OriginalPrice>
            {!price.strikethroughPrice && 'From'} {formatPrice(price.productPriceTotal)}
          </OriginalPrice>
        </Show>

        {shouldDisplayPromoCode && <PromoCode code={price.promoCode} />}
      </PricingPromoContainer>

      {valueProp && <ValuePropText>{valueProp}</ValuePropText>}
    </ProductHeaderContainer>
  );
};

export default ProductHeaderNew;
